// Where the session token lives, and for how long.
//
// TWO STORES, chosen at sign-in by the "remember this device" checkbox:
//
//   localStorage   — survives closing the browser. What somebody on their own laptop
//                    wants, and what this app has always done.
//   sessionStorage — dies with the tab. What somebody on a shared or borrowed machine
//                    wants, and the only thing that makes that checkbox mean anything.
//
// A checkbox wired to nothing is worse than no checkbox: it is a promise about a shared
// computer that the product does not keep. The token's SERVER-side lifetime is unchanged
// either way — there is no revocation endpoint yet, so an unticked box shortens exposure
// on that machine, not the token's validity. That distinction is worth knowing before
// anyone describes this as "log me out when I close the browser".
//
// Reads check both, newest intent first, so a session token wins over a stale remembered
// one on the same machine.
const TOKEN_KEY = "orai_token";

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
  } catch {
    // Storage can throw outright in a private window or with site data blocked. A signed
    // -out app is a recoverable state; a crashed one is not.
    return null;
  }
}

export function setToken(token: string, remember = true): void {
  try {
    // Written to one store and cleared from the other, so switching the checkbox on a
    // second sign-in cannot leave a forgotten copy behind in the one nobody is reading.
    if (remember) {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Nothing to do: the caller confirms the token against /me straight after, so a failed
    // write surfaces as a normal "could not sign in" rather than a silent half-session.
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Already unreachable; there is nothing left to clear.
  }
}
